import { z } from "zod";

const translationPath =
  "page.4uMarCom.automationRules.ticketautomationRules.errors.";

const ticketEventSchema = z.object(
  {
    id: z.string(),
    name: z.string(),
    code: z.string().nullish(),
  },
  { invalid_type_error: translationPath + "ticket_event" },
);

const actionTypeSchema = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string().optional().nullable(),
});

const ticketAutomationSchema = z.object(
  {
    id: z.string(),
    name: z.string(),
    code: z.string().optional().nullable(),
    isSendFile: z.boolean(),
    allowTransfer: z.boolean(),
    allowWhatsappTemplate: z.boolean(),
    allowForm: z.boolean(),
    actionTypes: z.array(actionTypeSchema).nullish(),
  },
  { invalid_type_error: translationPath + "ticket_automation" },
);

const ticketAutomationTemplateSchema = z.object(
  {
    id: z.string(),
    name: z.string(),
    code: z.string().nullish(),
  },
  { invalid_type_error: translationPath + "ticket_automation_template" },
);

const ticketChannelStepAttendantSchema = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string().nullish(),
});

const ticketChannelStepRobotSchema = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string().nullish(),
});

const ticketServiceSchema = z.object(
  {
    id: z.string(),
    name: z.string(),
    code: z.string().nullish(),
  },
  { invalid_type_error: translationPath + "ticket_service" },
);

const ticketChannelAttendantSchema = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string().nullish(),
});

const ticketChannelRobotSchema = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string().nullish(),
});

const ticketChannelStepPendent = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string().nullish(),
});

const ticketChannelStepWhenError = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string().nullish(),
});

const ticketChannelStepWhenBlacklist = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string().nullish(),
});

const actionAfter24HoursSchema = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string().nullish(),
});

export const marComTicketAutomationRulesSchema = z
  .object({
    id: z.string().optional(),
    enable: z.boolean(),
    name: z.string().min(1, translationPath + "name"),
    initialDate: z.date({
      invalid_type_error: translationPath + "initial_date",
    }),
    endDate: z.date({ invalid_type_error: translationPath + "end_date" }),
    ticketEvent: ticketEventSchema.nullish(),
    ticketAutomation: ticketAutomationSchema,
    ticketChannels: z.array(ticketChannelAttendantSchema).nullish(),
    ticketAutomationTemplate: ticketAutomationTemplateSchema.nullish(),
    actionAfter24Hours: actionAfter24HoursSchema.nullish(),
    ticketChannelStepAttendant: ticketChannelStepAttendantSchema.nullish(),
    ticketChannelStepRobot: ticketChannelStepRobotSchema.nullish(),
    ticketChannelStepPendent: ticketChannelStepPendent.nullish(),
    ticketChannelStepWhenError: ticketChannelStepWhenError.nullish(),
    ticketChannelStepWhenBlacklist: ticketChannelStepWhenBlacklist.nullish(),
    ticketChannelAttendant: ticketChannelAttendantSchema.nullish(),
    ticketChannelRobot: ticketChannelRobotSchema.nullish(),
    ticketChannelPendent: ticketChannelRobotSchema.nullish(),
    ticketChannelWhenError: ticketChannelRobotSchema.nullish(),
    ticketChannelWhenBlacklist: ticketChannelRobotSchema.nullish(),
    ticketService: ticketServiceSchema,
  })
  .superRefine((val, ctx) => {
    if (!val.ticketEvent) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: translationPath + "ticket_event",
        path: ["ticketEvent"],
      });
    }
    if (val.initialDate > val.endDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: translationPath + "initial_date_bigger",
        path: ["initialDate"],
      });
    }
    if (
      val.ticketAutomation.code !== "8" &&
      val.ticketAutomation.code !== "7"
    ) {
      if (!val.ticketChannelStepRobot) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: translationPath + "ticket_channel_step_robot",
          path: ["ticketChannelStepRobot"],
        });
      }
    }
    switch (val.ticketAutomation.code) {
      case "1":
      case "2":
      case "3":
      case "4":
      case "5":
      case "6":
        if (!val.ticketChannelStepPendent) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: translationPath + "ticket_channel_step_pendent",
            path: ["ticketChannelStepPendent"],
          });
        }
    }

    switch (val.ticketAutomation.code) {
      case "7":
        break;
      default:
        if (!val.ticketAutomationTemplate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: translationPath + "ticket_automation_template",
            path: ["ticketAutomationTemplate"],
          });
        }
    }

    if (!val.actionAfter24Hours) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: translationPath + "action_after_24_hours",
        path: ["actionAfter24Hours"],
      });
    }
  });

export type TMarComTicketAutomationRulesForm = z.infer<
  typeof marComTicketAutomationRulesSchema
>;
